import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const MAX_TRIES = 10;

export default function BaselineConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [token, setToken] = useState("");
  const tries = useRef(0);

  useEffect(() => {
    document.title = "Baseline Review Reserved — RetainProof";
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/public/baseline/status/${sessionId}`);
        if (data.paid) {
          setToken(data.intake_token); setStatus("paid");
          api.post("/public/baseline/track", { event: "confirmation_visit", session_id: sessionId }).catch(() => {});
          return;
        }
      } catch { /* keep polling */ }
      tries.current += 1;
      if (tries.current >= MAX_TRIES) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A1B2E] text-white font-body flex items-center justify-center p-6">
      <div className="grain-overlay" />
      <div className="relative z-10 w-full max-w-lg border border-white/10 bg-[#0B1F33] p-10 text-center" data-testid="baseline-confirmation-card">
        {status === "checking" && (<><Loader2 className="h-12 w-12 mx-auto text-zinc-400 animate-spin" /><h1 className="font-display text-3xl font-light mt-6">Confirming your reservation…</h1><p className="text-zinc-500 text-sm mt-2">This takes just a moment.</p></>)}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-[#0E7C7B]" />
            <h1 className="font-display text-3xl font-light mt-6" data-testid="baseline-confirmation-title">Your RetainProof Baseline Review Has Been Reserved</h1>
            <p className="text-zinc-400 text-sm mt-4 leading-relaxed">Thank you for selecting RetainProof. Your payment has secured your review production window. The next step is to complete the confidential intake and provide the available marketing invoices, vendor reports, lead data, intake information, retained-matter information, and other documentation needed for the analysis.</p>
            <p className="text-zinc-500 text-xs mt-3">Your review timeline will begin after the required information has been received and confirmed.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild className="rounded-none bg-[#D5A44A] text-black hover:bg-[#B8923D] h-11 px-6 group" data-testid="complete-intake-button">
                <Link to={`/baseline-intake/${token}`}>Complete Confidential Intake <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-none border-white/25 bg-transparent text-white hover:bg-white/5 h-11 px-6"><Link to="/">Return to RetainProof</Link></Button>
            </div>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="h-14 w-14 mx-auto text-[#B54747]" />
            <h1 className="font-display text-3xl font-light mt-6">We couldn't confirm this yet</h1>
            <p className="text-zinc-500 text-sm mt-2">{status === "timeout" ? "If you completed payment, it may take a moment to reflect. You'll also receive a confirmation email with your intake link." : "This confirmation link is missing its reference."}</p>
            <Button asChild variant="outline" className="mt-8 rounded-none border-white/25 bg-transparent text-white hover:bg-white/5 h-11 px-6"><Link to="/baseline-review">Back to Baseline Review</Link></Button>
          </>
        )}
      </div>
    </div>
  );
}
